import React, { useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";

const SubRecipeDialog = ({
  open,
  onClose,
  menuItems = [],
  onAdd, // (selectedMenuItems: Array<MenuItem>) => void
  excludeIds = [], // optional: array of menuItem._id to exclude (e.g., self)
  allowMultiple = true, // default multi-select
}) => {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState({}); // { [id]: true }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return menuItems
      .filter((m) => !excludeIds.includes(m._id))
      .filter((m) =>
        !q ? true : (m.menuName || "").toLowerCase().includes(q)
      );
  }, [menuItems, query, excludeIds]);

  const toggle = (id) => {
    if (!allowMultiple) {
      setSelected({ [id]: true });
      return;
    }
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleAdd = () => {
    const picked = menuItems.filter((m) => !!selected[m._id]);
    if (picked.length) onAdd(picked);
    setSelected({});
    setQuery("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Select Sub-Recipe</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          margin="dense"
          label="Search menu items"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />

        <List dense>
          {filtered.map((m) => {
            const checked = !!selected[m._id];
            const ingredientsLine =
              Array.isArray(m.menuIngredients) && m.menuIngredients.length > 0
                ? m.menuIngredients
                    .map((ing) =>
                      ing.subRecipeId
                        ? `[SubRecipe] ${ing.subRecipeName}`
                        : ing.ingredientName
                    )
                    .filter(Boolean)
                    .join(", ")
                : "";

            return (
              <ListItem key={m._id} disablePadding secondaryAction={null}>
                <ListItemButton onClick={() => toggle(m._id)}>
                  <Checkbox
                    edge="start"
                    checked={checked}
                    tabIndex={-1}
                    disableRipple
                    onClick={(e) => e.stopPropagation()} // so clicking the box doesn't double toggle
                    onChange={() => toggle(m._id)}
                  />
                  <ListItemText
                    primary={
                      <>
                        <Typography variant="subtitle1">
                          {m.menuName}
                        </Typography>
                        {m.categoryName && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ display: "block" }}
                          >
                            Category: {m.categoryName}
                          </Typography>
                        )}
                      </>
                    }
                    secondary={
                      ingredientsLine ? (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          component="div"
                          sx={{ mt: 0.5 }}
                        >
                          Ingredients: {ingredientsLine}
                        </Typography>
                      ) : null
                    }
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Button
          onClick={handleAdd}
          color="primary"
          disabled={!Object.values(selected).some(Boolean)}
        >
          Add Selected
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubRecipeDialog;
